// src/components/questionanswer/QnAHibernate.jsx
import React, { useEffect, useMemo, useState } from "react";
import questions from "./qnaJSONdata/QnAHibernate.json";
import QnASideBarRight from "./QnASideBarRight";
import ScrollButton from "../../ScrollButton";

/**
 * QnAHibernate + Download PDF
 * - LEFT: sticky "On this page" + sponsored ad
 * - MAIN: inline questions with filter; header includes Download PDF + Print
 * - RIGHT: existing sidebar (already has a Sponsored card)
 */

// ============== AdSense helpers (same as Java/Spring) ==============
const ADSENSE_CLIENT = "ca-pub-9033879555371063"; // your client id

function useAdSense(clientId) {
  useEffect(() => {
    if (!clientId) return;
    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const s = document.createElement("script");
      s.setAttribute("data-adsbygoogle", "true");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    } else {
      try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch {}
    }
  }, [clientId]);
}

function AdSlot({
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  style = { display: "block", minHeight: 90 },
  className = "",
}) {
  useEffect(() => {
    try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch {}
  }, []);
  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

// ============== Page bits ==============
const normalize = (s = "") => s.toLowerCase();

// Allow override via .env: REACT_APP_HIBERNATE_PDF_URL
const HIBERNATE_PDF_URL =
  process.env.REACT_APP_HIBERNATE_PDF_URL ||
  `${process.env.PUBLIC_URL || ""}/pdfs/hibernate-interview-questions.pdf`;

function FilterBar({ value, onChange, total, shown }) {
  return (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
      <input
        className="form-control form-control-sm"
        style={{ maxWidth: 380 }}
        type="search"
        placeholder="Filter questions (e.g., session, cache, mapping, HQL)…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Filter questions"
      />
      <span className="text-muted small">Showing {shown} of {total}</span>
    </div>
  );
}

function QAInlineList({ items }) {
  return (
    <ol className="ps-3">
      {items.map((it, idx) => (
        <li key={it.id ?? idx} className="mb-3">
          <h5 className="mb-2">{it.question}</h5>
          <div className="text-body" style={{ whiteSpace: "pre-wrap" }}>{it.answer}</div>
        </li>
      ))}
    </ol>
  );
}

function OnThisPageLeft() {
  return (
    <div className="card shadow-sm border-0">
      <div className="card-body">
        <h2 className="h6 text-uppercase text-muted mb-2">On this page</h2>
        <nav className="nav flex-column small">
          <a className="nav-link px-0" href="#hibernate">Hibernate Q&amp;A</a>
          <a
            className="nav-link px-0 mt-2 text-decoration-underline"
            href={HIBERNATE_PDF_URL}
            download="TechVenture-Hibernate-Interview-Questions.pdf"
            aria-label="Download Hibernate Q&A as PDF"
          >
            ⬇︎ Download PDF
          </a>
        </nav>
      </div>
    </div>
  );
}

function LeftSponsoredCard() {
  return (
    <div className="card shadow-sm border-0 mt-3">
      <div className="card-body">
        <h2 className="h6 text-uppercase text-muted mb-2">Sponsored</h2>
        <AdSlot adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </div>
    </div>
  );
}

// ============== Main component ==============
export default function QnAHibernate() {
  useAdSense(ADSENSE_CLIENT);

  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const qn = normalize(query);
    if (!qn) return questions;
    return questions.filter((x) => normalize(x.question + " " + x.answer).includes(qn));
  }, [query]);

  return (
    <div className="container-fluid">
      <div className="row g-4 flex-xl-nowrap">
        {/* LEFT: On This Page + Sponsored ad (sticky) */}
        <aside className="d-none d-xl-block col-xl-2 pt-3">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <OnThisPageLeft />
            <LeftSponsoredCard />
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7 pt-3" role="main">
          <header className="mb-2">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>
                <h1 className="h2 mb-1">Hibernate Interview Questions</h1>
                <p className="text-muted mb-0">
                  Use the filter to quickly find topics like session lifecycle, caching, mappings,
                  HQL/Criteria, transactions, and performance tuning.
                </p>
              </div>
              <div className="btn-group">
                <a
                  href={HIBERNATE_PDF_URL}
                  className="btn btn-outline-danger btn-sm"
                  download="TechVenture-Hibernate-Interview-Questions.pdf"
                  aria-label="Download Hibernate Interview Questions PDF"
                >
                  Download PDF
                </a>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => window.print()}
                  aria-label="Print this page"
                >
                  Print
                </button>
              </div>
            </div>
          </header>

          <FilterBar
            value={query}
            onChange={setQuery}
            total={questions.length}
            shown={filtered.length}
          />

          {/* In-article ad (centered) */}
          <div className="my-3 text-center">
            <AdSlot adSlot="5201219465" style={{ display: "block", minHeight: 90, textAlign: "center" }} />
          </div>

          <section id="hibernate" className="pt-2">
            <QAInlineList items={filtered} />
          </section>

          <footer className="border-top mt-4 pt-3 small text-muted">
            Tip: Draw the entity graph and the session/transaction boundaries when answering performance and N+1 questions.
          </footer>

          <ScrollButton />
        </main>

        {/* RIGHT: existing sidebar (already has "Sponsored" card) */}
        <aside className="col-12 col-lg-4 col-xl-3 pt-3 border-start">
          <QnASideBarRight />
        </aside>
      </div>
    </div>
  );
}
